import { ReloadOutlined } from '@ant-design/icons';
import type { InvoiceRow, PaymentRow, SettleItemRow, SettleRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { SETTLE_STATUS_META, formatAmount, formatDisplayTime, formatQty } from '@hq/utils';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Drawer,
  Input,
  InputNumber,
  Modal,
  Segmented,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { hq } from '../session';
import { ellipsisCell, sumWidth } from '@hq/ui-kit';

const PAGE_SIZE = 20;

// 状态表统一在 `@hq/utils`（三端曾各存一份，配色还分叉）
const STATUS_META = SETTLE_STATUS_META;

/**
 * 对账开票（供应商端）：只看收款方为当前供应商的对账单。
 * 确认、开票与争议申诉待供应商协同端点就绪后接入。
 */
type StatusFilter = 'ALL' | 'SENT' | 'CONFIRMED' | 'INVOICED' | 'PART_PAID' | 'PAID' | 'DISPUTED';

const FILTERS: { label: string; value: StatusFilter }[] = [
  { label: '全部', value: 'ALL' },
  { label: '待确认', value: 'SENT' },
  { label: '已确认', value: 'CONFIRMED' },
  { label: '已开票', value: 'INVOICED' },
  { label: '部分付款', value: 'PART_PAID' },
  { label: '已付清', value: 'PAID' },
  { label: '有争议', value: 'DISPUTED' }
];

/** 可发起争议的状态：与后端 DisputeAsync 的 settle_status 白名单一致。 */
const DISPUTABLE_STATUSES = ['DRAFT', 'GENERATED', 'SENT', 'CONFIRMED', 'INVOICED', 'PART_PAID', 'RE_ADJUSTED'];

/** 可开票的状态：已确认之后、付清/关闭之前（争议中不可开票）。 */
const INVOICABLE_STATUSES = ['CONFIRMED', 'INVOICED', 'PART_PAID'];

export default function SettleSupplier() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [status, setStatus] = useState<StatusFilter>('ALL');
  const [period, setPeriod] = useState('');

  const [disputeOpen, setDisputeOpen] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');
  const [disputeAmount, setDisputeAmount] = useState<number | null>(null);

  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [invoiceNo, setInvoiceNo] = useState('');
  const [invoiceCode, setInvoiceCode] = useState('');
  const [invoiceAmount, setInvoiceAmount] = useState<number | null>(null);
  const [invoiceTax, setInvoiceTax] = useState<number | null>(0);
  const [invoiceAt, setInvoiceAt] = useState('');

  const dispute = useMutation({
    mutationFn: () =>
      hq.billing.settle.dispute(currentId ?? '', {
        reason: disputeReason.trim(),
        disputeAmount: disputeAmount ?? undefined
      }),
    onSuccess: async () => {
      message.success('争议已提交，等待院方裁定');
      setDisputeOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['supplier', 'settle'] });
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '提交争议失败')
  });

  const invoices = useQuery({
    queryKey: ['supplier', 'settle', 'invoice', currentId],
    placeholderData: keepPreviousData,
    queryFn: () => hq.billing.settle.invoices(currentId ?? ''),
    enabled: Boolean(currentId)
  });

  const payments = useQuery({
    queryKey: ['supplier', 'settle', 'payment', currentId],
    placeholderData: keepPreviousData,
    queryFn: () => hq.billing.settle.payments(currentId ?? ''),
    enabled: Boolean(currentId)
  });

  const invoice = useMutation({
    mutationFn: () =>
      hq.billing.settle.invoice(currentId ?? '', {
        invoiceNo: invoiceNo.trim(),
        invoiceCode: invoiceCode.trim() || undefined,
        amount: invoiceAmount ?? 0,
        taxAmount: invoiceTax ?? undefined,
        invoiceAt: invoiceAt || undefined
      }),
    onSuccess: async () => {
      message.success('发票已提交，院方财务确认后安排付款');
      setInvoiceOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['supplier', 'settle'] });
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '登记发票失败')
  });

  const query = useInfiniteQuery({
    queryKey: ['supplier', 'settle', 'list', status, period],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      hq.billing.settle.list({
        lastId: pageParam,
        size: PAGE_SIZE,
        payeeOnly: true,
        settleStatus: status === 'ALL' ? undefined : status,
        settlePeriod: period || undefined
      }),
    // hasMore 为真但 nextLastId 为空时不能把 null 当成游标：否则会重复拉首页
    getNextPageParam: (lastPage) => (lastPage.hasMore && lastPage.nextLastId ? lastPage.nextLastId : undefined)
  });

  const detail = useQuery({
    queryKey: ['supplier', 'settle', 'detail', currentId],
    placeholderData: keepPreviousData,
    queryFn: () => hq.billing.settle.detail(currentId ?? '', { payeeOnly: true }),
    enabled: Boolean(currentId)
  });

  const rows = query.data?.pages.flatMap((page) => page.items) ?? [];

  const columns: ColumnsType<SettleRow> = [
    {
      title: '对账单号',
      dataIndex: 'bizNo',
      width: 220,
      render: (value: string, row) => <Typography.Link onClick={() => setCurrentId(row.id)}>{value}</Typography.Link>
    },
    { title: '结算期间', dataIndex: 'settlePeriod', width: 120, render: (value: string | null) => value || '-' },
    { title: '应结金额', dataIndex: 'settleAmount', width: 130, align: 'right', render: (value: number) => formatAmount(value) },
    {
      title: '退货扣减',
      dataIndex: 'returnAmount',
      width: 130,
      align: 'right',
      render: (value: number) =>
        value < 0 ? <Typography.Text type="danger">{formatAmount(value)}</Typography.Text> : formatAmount(value)
    },
    { title: '应付金额', dataIndex: 'payableAmount', width: 130, align: 'right', render: (value: number) => formatAmount(value) },
    { title: '已付金额', dataIndex: 'paidAmount', width: 130, align: 'right', render: (value: number) => formatAmount(value) },
    {
      title: '状态',
      dataIndex: 'settleStatus',
      width: 110,
      render: (value: string) => {
        const meta = STATUS_META[value] ?? { text: value, color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    { title: '确认时间', dataIndex: 'confirmAt', width: 160, render: (value: string | null) => formatDisplayTime(value) }
  ];

  const itemColumns: ColumnsType<SettleItemRow> = [
    { title: '行', dataIndex: 'lineNo', width: 60 },
    { title: '业务类型', dataIndex: 'bizType', width: 120, render: (value: string | null) => value || '-' },
    { title: '业务单号', dataIndex: 'bizNo', width: 190, render: (value: string | null) => value || '-' },
    { title: '物资', dataIndex: 'materialName', ellipsis: { showTitle: false }, render: ellipsisCell('materialName') },
    { title: '数量', dataIndex: 'qty', width: 90, align: 'right', render: (value: number) => formatQty(value) },
    {
      title: '金额',
      dataIndex: 'amount',
      width: 130,
      align: 'right',
      render: (value: number) =>
        value < 0 ? <Typography.Text type="danger">{formatAmount(value)}</Typography.Text> : formatAmount(value)
    },
    {
      title: '方向',
      dataIndex: 'direction',
      width: 90,
      render: (value: number) => (value < 0 ? <Tag color="red">扣减</Tag> : <Tag color="green">计费</Tag>)
    },
    { title: '发生时间', dataIndex: 'occurAt', width: 160, render: (value: string | null) => formatDisplayTime(value) }
  ];

  const invoiceColumns: ColumnsType<InvoiceRow> = [
    { title: '发票号', dataIndex: 'invoiceNo', width: 160 },
    { title: '代码', dataIndex: 'invoiceCode', width: 120, render: (value: string | null) => value || '-' },
    { title: '价税合计', dataIndex: 'totalAmount', width: 130, align: 'right', render: (value: number) => formatAmount(value) },
    { title: '税额', dataIndex: 'taxAmount', width: 110, align: 'right', render: (value: number) => formatAmount(value) },
    { title: '开票时间', dataIndex: 'invoiceAt', width: 150, render: (value: string | null) => formatDisplayTime(value) }
  ];

  const paymentColumns: ColumnsType<PaymentRow> = [
    { title: '付款单号', dataIndex: 'payNo', width: 180 },
    { title: '金额', dataIndex: 'payAmount', width: 130, align: 'right', render: (value: number) => formatAmount(value) },
    { title: '方式', dataIndex: 'payMethod', width: 110, render: (value: string | null) => value || '-' },
    { title: '付款时间', dataIndex: 'payAt', width: 150, render: (value: string | null) => formatDisplayTime(value) }
  ];

  const account = detail.data?.account;
  const unpaid = account ? Math.max(0, account.payableAmount - account.paidAmount) : 0;

  return (
    <Card
      className="page-card"
      title="对账开票"
      extra={
        <Button icon={<ReloadOutlined />} onClick={() => query.refetch()} loading={query.isFetching}>
          刷新
        </Button>
      }
    >
      <Space style={{ marginBottom: 12 }} wrap>
        <Segmented<StatusFilter>
          value={status}
          options={FILTERS}
          onChange={(value) => setStatus(value)}
        />
        <Input
          allowClear
          placeholder="结算期间 yyyy-MM"
          value={period}
          onChange={(event) => setPeriod(event.target.value)}
          style={{ width: 170 }}
        />
      </Space>

      {query.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="对账单加载失败"
          description={query.error instanceof ApiError ? query.error.friendlyMessage : String(query.error)}
        />
      ) : null}

      <Table<SettleRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={query.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: query.isError ? '加载失败，请重试' : '暂无对账单' }}
        onRow={(row) => ({ onDoubleClick: () => setCurrentId(row.id) })}
      />

      <Space style={{ marginTop: 16 }}>
        <Typography.Text type="secondary">已加载 {rows.length} 条</Typography.Text>
        {query.hasNextPage ? (
          <Button onClick={() => query.fetchNextPage()} loading={query.isFetchingNextPage}>
            加载更多
          </Button>
        ) : rows.length > 0 ? (
          <Typography.Text type="secondary">没有更多了</Typography.Text>
        ) : null}
      </Space>

      <Drawer
        title={detail.data?.account.bizNo ?? '对账单详情'}
        open={currentId !== null}
        onClose={() => setCurrentId(null)}
        width={900}
      >
        {detail.isLoading ? (
          <Spin />
        ) : detail.isError ? (
          <Alert
            type="error"
            showIcon
            message="详情加载失败"
            description={detail.error instanceof ApiError ? detail.error.friendlyMessage : String(detail.error)}
          />
        ) : detail.data ? (
          <>
            <Descriptions size="small" column={2} bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="期间">{detail.data.account.settlePeriod || '-'}</Descriptions.Item>
              <Descriptions.Item label="状态">
                {STATUS_META[detail.data.account.settleStatus]?.text ?? detail.data.account.settleStatus}
              </Descriptions.Item>
              <Descriptions.Item label="应结">{formatAmount(detail.data.account.settleAmount)}</Descriptions.Item>
              <Descriptions.Item label="退货扣减">{formatAmount(detail.data.account.returnAmount)}</Descriptions.Item>
              <Descriptions.Item label="应付">{formatAmount(detail.data.account.payableAmount)}</Descriptions.Item>
              <Descriptions.Item label="已付">{formatAmount(detail.data.account.paidAmount)}</Descriptions.Item>
              <Descriptions.Item label="备注" span={2}>
                {detail.data.account.remark || '-'}
              </Descriptions.Item>
            </Descriptions>

            {DISPUTABLE_STATUSES.includes(detail.data.account.settleStatus) ? (
              <Space style={{ marginBottom: 16 }}>
                <Button
                  danger
                  onClick={() => {
                    setDisputeReason('');
                    setDisputeAmount(null);
                    setDisputeOpen(true);
                  }}
                >
                  登记争议
                </Button>
                <Typography.Text type="secondary">
                  对账金额/数量有异议时提交，院方裁定前不可继续开票付款。
                </Typography.Text>
              </Space>
            ) : null}

            {INVOICABLE_STATUSES.includes(detail.data.account.settleStatus) ? (
              <Space style={{ marginBottom: 16 }}>
                <Button
                  type="primary"
                  onClick={() => {
                    setInvoiceNo('');
                    setInvoiceCode('');
                    setInvoiceAmount(unpaid > 0 ? Number(unpaid.toFixed(2)) : null);
                    setInvoiceTax(0);
                    setInvoiceAt('');
                    setInvoiceOpen(true);
                  }}
                >
                  登记发票
                </Button>
                <Typography.Text type="secondary">
                  录入发票价税合计（未付 {formatAmount(unpaid)}），院方财务确认后安排付款。
                </Typography.Text>
              </Space>
            ) : null}

            <Tabs
              items={[
                {
                  key: 'invoice',
                  label: `发票（${invoices.data?.length ?? 0}）`,
                  children: (
                    <Table<InvoiceRow>
                      rowKey="id"
                      size="small"
                      columns={invoiceColumns}
                      dataSource={invoices.data ?? []}
                      loading={Boolean(currentId) && invoices.isPending}
                      pagination={false}
                      scroll={{ x: sumWidth(invoiceColumns) }}
                      locale={{ emptyText: '尚未登记发票' }}
                    />
                  )
                },
                {
                  key: 'payment',
                  label: `付款（${payments.data?.length ?? 0}）`,
                  children: (
                    <Table<PaymentRow>
                      rowKey="id"
                      size="small"
                      columns={paymentColumns}
                      dataSource={payments.data ?? []}
                      loading={Boolean(currentId) && payments.isPending}
                      pagination={false}
                      scroll={{ x: sumWidth(paymentColumns) }}
                      locale={{ emptyText: '院方尚未付款' }}
                    />
                  )
                },
                {
                  key: 'item',
                  label: '结算明细',
                  children: (
                    <Table<SettleItemRow>
                      rowKey="id"
                      size="small"
                      columns={itemColumns}
                      dataSource={detail.data.items}
                      pagination={false}
                      scroll={{ x: sumWidth(itemColumns) }}
                    />
                  )
                }
              ]}
            />
          </>
        ) : null}
      </Drawer>

      <Modal
        title="登记发票"
        open={invoiceOpen}
        onCancel={() => setInvoiceOpen(false)}
        onOk={() => {
          if (invoiceNo.trim().length < 4) {
            message.warning('请填写发票号码');
            return;
          }
          if (!invoiceAmount || invoiceAmount <= 0) {
            message.warning('请填写价税合计');
            return;
          }
          invoice.mutate();
        }}
        okText="提交发票"
        confirmLoading={invoice.isPending}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message={`未付金额 ${formatAmount(unpaid)}`}
            description="录入的是发票价税合计；累计开票金额不得超过应付含税，超额会被院方财务退回。"
          />
          <Space size={12} wrap>
            <Input
              placeholder="发票号码"
              style={{ width: 220 }}
              maxLength={32}
              value={invoiceNo}
              onChange={(event) => setInvoiceNo(event.target.value)}
            />
            <Input
              placeholder="发票代码（选填）"
              style={{ width: 200 }}
              maxLength={32}
              value={invoiceCode}
              onChange={(event) => setInvoiceCode(event.target.value)}
            />
          </Space>
          <Space size={12} wrap>
            <InputNumber
              placeholder="价税合计"
              style={{ width: 180 }}
              min={0}
              precision={2}
              value={invoiceAmount}
              onChange={(value) => setInvoiceAmount(typeof value === 'number' ? value : null)}
            />
            <InputNumber
              placeholder="其中税额"
              style={{ width: 160 }}
              min={0}
              precision={2}
              value={invoiceTax}
              onChange={(value) => setInvoiceTax(typeof value === 'number' ? value : 0)}
            />
            <DatePicker
              placeholder="开票日期"
              onChange={(value) => setInvoiceAt(value ? value.format('YYYY-MM-DD') : '')}
            />
          </Space>
        </Space>
      </Modal>

      <Modal
        title="登记争议"
        open={disputeOpen}
        onCancel={() => setDisputeOpen(false)}
        onOk={() => {
          if (disputeReason.trim().length < 2) {
            message.warning('请填写争议原因');
            return;
          }
          dispute.mutate();
        }}
        okText="提交争议"
        okButtonProps={{ danger: true }}
        confirmLoading={dispute.isPending}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Input.TextArea
            rows={3}
            maxLength={300}
            showCount
            placeholder="争议原因（数量/质量/价格差异等）"
            value={disputeReason}
            onChange={(event) => setDisputeReason(event.target.value)}
          />
          <InputNumber
            style={{ width: '100%' }}
            placeholder="争议金额（可选）"
            min={0}
            precision={2}
            value={disputeAmount}
            onChange={(value) => setDisputeAmount(value === null ? null : Number(value))}
          />
          <Typography.Text type="secondary">
            仅可对收款方为本企业的对账单发起争议；提交后状态置为「有争议」。
          </Typography.Text>
        </Space>
      </Modal>
    </Card>
  );
}
